import { supabase, DEFAULT_ORG_ID } from '../supabaseClient'
import { getCurrentSubscription } from './billing'
import { STARTER_DEFAULTS } from '../entitlements'

export const getOrganization = async (orgId) => {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', orgId)
    .single()

  return { data, error }
}

// Organization deportes
export const getOrgDeportes = async (orgId) => {
  if (!orgId) return { data: [], error: null }

  const { data, error } = await supabase
    .from('organization_deportes')
    .select('*, deporte:deportes(*)')
    .eq('org_id', orgId)
    .eq('active', true)

  return { data, error }
}

export const setOrgDeportes = async (orgId, deporteIds, { enforceLimit = false } = {}) => {
  // Tope de deportes del plan de la org. NO aplica el bypass de platform admin:
  // es un guardarrail al configurar deportes desde Platform Admin. El alta de
  // org NO pasa enforceLimit (el plan aun no esta asignado).
  if (enforceLimit) {
    const { data: sub } = await getCurrentSubscription(orgId)
    if (!sub?.grandfathered_forever) {
      const limit = sub?.plan?.limits?.deportes_max ?? STARTER_DEFAULTS.limits.deportes_max
      if ((deporteIds?.length || 0) > limit) {
        return {
          data: null,
          error: {
            code: 'PLAN_LIMIT',
            resource: 'deportes',
            message: `El plan de esta organización permite ${limit} deporte(s). Sube el plan de la org para habilitar más.`,
          },
        }
      }
    }
  }

  // Desactivar todos los actuales
  await supabase
    .from('organization_deportes')
    .update({ active: false })
    .eq('org_id', orgId)

  // Insertar/reactivar los seleccionados
  const rows = deporteIds.map(id => ({ org_id: orgId, deporte_id: id, active: true }))
  const { data, error } = await supabase
    .from('organization_deportes')
    .upsert(rows, { onConflict: 'org_id,deporte_id' })
    .select('*, deporte:deportes(*)')

  return { data, error }
}

export const getAllDeportes = async () => {
  const { data, error } = await supabase
    .from('deportes')
    .select('*')
    .eq('activo', true)
    .order('nombre')

  return { data, error }
}

export const createOrganization = async (orgData) => {
  const baseSlug = (orgData.slug || orgData.name.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')) || 'org'

  const payload = {
    name: orgData.name,
    short_name: orgData.short_name || null,
    type: orgData.type || 'club',
    active: true,
    color_primary: orgData.color_primary || '#000000',
    color_secondary: orgData.color_secondary || '#B5B8BE',
    color_background: orgData.color_background || '#F2F2F3',
    color_icon_background: orgData.color_icon_background || null,
    pais: orgData.pais || null,
    estado: orgData.estado || null,
    ciudad: orgData.ciudad || null,
    // Datos del responsable con los que se arma el Aviso de Privacidad del club.
    // Van en esta lista blanca a proposito: lo que no este aqui se descarta en
    // silencio, y una org sin ellos nace exigiendo consentimiento con un Aviso
    // que dice "[por definir]" donde deberia decir quien responde por los datos.
    razon_social: orgData.razon_social || null,
    domicilio_fiscal: orgData.domicilio_fiscal || null,
    correo_privacidad: orgData.correo_privacidad || null,
  }

  // El slug es UNIQUE a nivel global (las dos apps comparten `organizations`), asi que la
  // misma marca en Club y en Torneos (p. ej. "Elite") chocaria. Se DIFERENCIA POR TIPO:
  // primero el slug base, luego con sufijo del tipo (liga/torneos/academia/...), y como
  // ultima red un sufijo numerico. Evita fallar con organizations_slug_key.
  const TYPE_SLUG = {
    league: 'liga', tournament_organizer: 'torneos', academy: 'academia',
    club: 'club', federation: 'federacion',
  }
  const typeSuffix = TYPE_SLUG[payload.type]
  const candidatos = [baseSlug]
  if (typeSuffix) candidatos.push(`${baseSlug}-${typeSuffix}`)
  for (let n = 2; n <= 30; n++) candidatos.push(`${baseSlug}-${n}`)

  let lastError = null
  for (const slug of candidatos) {
    const { data, error } = await supabase
      .from('organizations')
      .insert([{ ...payload, slug }])
      .select()
      .single()
    if (!error) return { data, error: null }
    const isSlugDup = error.code === '23505' && /slug/i.test(error.message || '')
    if (!isSlugDup) return { data: null, error }
    lastError = error
  }
  return { data: null, error: lastError || { message: 'No se pudo generar un identificador \u00fanico para la organizaci\u00f3n.' } }
}

export const createClubWithAdmin = async ({
  clubName,
  shortName,
  adminEmail,
  adminName,
  deporteIds,
  type = 'club',
  colorPrimary,
  colorSecondary,
  colorBackground,
  colorIconBackground,
  pais,
  estado,
  ciudad,
  // Datos del responsable para el Aviso de Privacidad. Desde 20260819 las orgs
  // nacen exigiendo consentimiento; sin estos, el club arrancaria pidiendo firmas
  // sobre un Aviso que dice "[por definir]" en responsable, domicilio y via ARCO.
  razonSocial = null,
  domicilioFiscal = null,
  correoPrivacidad = null
}) => {
  // 1. Crear la org
  const { data: org, error: orgError } = await createOrganization({
    name: clubName,
    short_name: shortName || null,
    type: type || 'club',
    color_primary: colorPrimary,
    color_secondary: colorSecondary,
    color_background: colorBackground,
    color_icon_background: colorIconBackground || null,
    pais,
    estado,
    ciudad,
    razon_social: razonSocial || null,
    domicilio_fiscal: domicilioFiscal || null,
    correo_privacidad: correoPrivacidad || null,
  })

  if (orgError) return { data: null, error: orgError }

  // 1b. Asignar deportes
  const selectedDeportes = deporteIds?.length ? deporteIds : ['flag']
  await setOrgDeportes(org.id, selectedDeportes)

  // 1c. Sembrar categorias por defecto segun los deportes activados
  const { error: seedError } = await supabase.rpc('seed_default_categories', {
    p_org_id: org.id,
  })
  if (seedError) {
    console.warn('No se pudieron sembrar categorias por defecto:', seedError)
  }

  // 2. Alta del admin fundador. Reusa upsert_org_member (SECURITY DEFINER): si el correo
  // YA existe como persona (p. ej. admin de otra org / de la otra app), le AGREGA la
  // membresia de admin en vez de fallar por email duplicado (coaches_email_key). Crea la
  // membresia por dentro, asi que ya no hace falta insertarla aparte.
  const emailNorm = adminEmail.toLowerCase().trim()
  const nombreNorm = adminName || adminEmail.split('@')[0]
  const { data: upsertRows, error: upsertError } = await supabase.rpc('upsert_org_member', {
    p_org_id: org.id,
    p_email: emailNorm,
    p_nombre: nombreNorm,
    p_telefono: null,
    p_rol: 'admin',
    p_is_founder: true,
  })

  if (upsertError) {
    // Rollback: la org ya se creo, borrarla para no dejar huerfanas
    // (org_deportes y categorias caen por ON DELETE CASCADE)
    await supabase.from('organizations').delete().eq('id', org.id)
    return { data: null, error: upsertError }
  }

  const upsertRow = Array.isArray(upsertRows) ? upsertRows[0] : upsertRows
  const staffData = { id: upsertRow?.staff_id, email: emailNorm, nombre_completo: nombreNorm }

  // 3. Si la persona YA existia (otra org), ya tiene cuenta MYKLUBB: entra a la nueva org
  // con su mismo correo, sin nueva invitacion. Solo se invita a personas nuevas.
  if (upsertRow?.outcome !== 'created_new') {
    return {
      data: { org, staff: staffData, reused: true },
      error: null,
      warning: `La organizacion se creo y se agrego a "${nombreNorm}" como administrador. Esa persona ya tenia una cuenta MYKLUBB: entra con su mismo correo (${emailNorm}), no se envio una nueva invitacion.`,
    }
  }

  // 4. Persona nueva: crear usuario auth e invitar via edge function
  const { data: authResult, error: authError } = await supabase.functions.invoke(
    'create-staff-auth',
    {
      body: {
        staff_id: staffData.id,
        email: emailNorm,
        nombre_completo: nombreNorm,
        rol: 'admin',
      },
    }
  )

  if (authError) {
    // La org y el staff ya se crearon, reportar el error pero no fallar completamente
    return {
      data: { org, staff: staffData, authError: authError.message },
      error: null,
      warning: `Club creado pero la invitacion fallo: ${authError.message}. Puedes reenviarla manualmente.`
    }
  }

  // El usuario auth se creo, pero el correo de invitacion pudo fallar en el envio
  // (SMTP mal configurado, buzon rechazado, limite excedido). No es silencioso.
  if (authResult?.email_sent === false) {
    return {
      data: { org, staff: staffData, auth: authResult },
      error: null,
      warning: `Club y usuario creados, pero el correo de invitacion no se pudo enviar${authResult.email_error ? ` (${authResult.email_error})` : ''}. Reenvialo desde Staff cuando se resuelva el envio de correos.`
    }
  }

  return {
    data: { org, staff: staffData, auth: authResult },
    error: null,
  }
}

export const getAllOrganizations = async () => {
  const { data, error } = await supabase
    .from('organizations')
    .select('*, organization_deportes(deporte_id, active)')
    .order('name')

  return { data, error }
}

export const toggleOrganizationActive = async (orgId, active) => {
  const { data, error } = await supabase
    .from('organizations')
    .update({ active, updated_at: new Date().toISOString() })
    .eq('id', orgId)
    .select()
    .single()

  return { data, error }
}

export const updateOrganization = async (orgId, updates) => {
  const { data, error } = await supabase
    .from('organizations')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', orgId)
    .select()
    .single()

  return { data, error }
}

export const uploadOrganizationLogo = async (orgId, file) => {
  const fileExt = file.name.split('.').pop()
  const filePath = `organizations/${orgId}/logo.${fileExt}`

  const { error: uploadError } = await supabase.storage
    .from('logos')
    .upload(filePath, file, { upsert: true })

  if (uploadError) return { data: null, error: uploadError }

  const { data: urlData } = supabase.storage
    .from('logos')
    .getPublicUrl(filePath)

  const logoUrl = urlData?.publicUrl

  const { data, error } = await updateOrganization(orgId, { logo_url: logoUrl })
  return { data, error }
}
